import type { IBookingService } from '@types'
import { defineStore } from 'pinia'

export interface IBookingCartPeriod {
  startAt: string
  endAt: string
}

export interface IBookingCartInventoryItem {
  service: IBookingService
  quantity: number
  unavailableQuantity?: number
}

export const useBookingCartStore = defineStore(
  'booking-cart',
  () => {
    const companyAlias = ref('')
    const companyCarts = ref<
      Record<string, { period?: IBookingCartPeriod; ids: number[]; items: Record<number, IBookingCartInventoryItem> }>
    >({})
    const period = ref<IBookingCartPeriod>()
    const inventoryServiceIds = ref<number[]>([])
    const inventoryItemsById = ref<Record<number, IBookingCartInventoryItem>>({})

    const inventoryItems = computed(() => Object.values(inventoryItemsById.value))
    const firstInventoryServiceId = computed(
      () => inventoryServiceIds.value.find(id => inventoryItemsById.value[id]) ?? inventoryItems.value[0]?.service.id,
    )
    const availableInventoryItems = computed(() =>
      inventoryItems.value
        .map(item => ({
          ...item,
          quantity: item.quantity - getUnavailableQuantity(item),
        }))
        .filter(item => item.quantity > 0),
    )
    const unavailableInventoryItems = computed(() =>
      inventoryItems.value
        .map(item => ({
          ...item,
          quantity: getUnavailableQuantity(item),
        }))
        .filter(item => item.quantity > 0),
    )
    const unavailableQuantity = computed(() =>
      unavailableInventoryItems.value.reduce((total, item) => total + item.quantity, 0),
    )
    const totalQuantity = computed(() => inventoryItems.value.reduce((total, item) => total + item.quantity, 0))

    function setCompany(alias: string, migrateLegacy = false) {
      if (!alias || companyAlias.value === alias) {
        return
      }
      // Older carts had no owner. Only adopt one when the persisted company is known.
      if (!companyAlias.value && migrateLegacy) {
        companyAlias.value = alias
        return
      }
      if (companyAlias.value) {
        companyCarts.value[companyAlias.value] = {
          period: period.value,
          ids: inventoryServiceIds.value,
          items: inventoryItemsById.value,
        }
      }
      const saved = companyCarts.value[alias]
      period.value = saved?.period
      inventoryServiceIds.value = saved?.ids || []
      inventoryItemsById.value = saved?.items || {}
      companyAlias.value = alias
    }

    function getUnavailableQuantity(item: IBookingCartInventoryItem) {
      return item.unavailableQuantity ?? Math.max(0, item.quantity - (item.service.availableQuantity ?? item.quantity))
    }

    function setPeriod(start: Date, end: Date) {
      period.value = {
        startAt: start.toISOString(),
        endAt: end.toISOString(),
      }
    }

    function getInventoryQuantity(serviceId: number) {
      return inventoryItemsById.value[serviceId]?.quantity || 0
    }

    function setInventoryQuantity(service: IBookingService, quantity: number) {
      const normalizedQuantity = Number.isFinite(quantity) ? Math.max(0, Math.floor(quantity)) : 0

      if (!normalizedQuantity) {
        delete inventoryItemsById.value[service.id]
        inventoryServiceIds.value = inventoryServiceIds.value.filter(id => id !== service.id)
        return
      }

      if (!inventoryServiceIds.value.length) {
        inventoryServiceIds.value = inventoryItems.value.map(item => item.service.id)
      }
      if (!inventoryServiceIds.value.includes(service.id)) {
        inventoryServiceIds.value.push(service.id)
      }
      inventoryItemsById.value[service.id] = {
        service,
        quantity: normalizedQuantity,
      }
    }

    function setAvailableInventoryQuantity(service: IBookingService, quantity: number) {
      const unavailable = unavailableInventoryItems.value.find(item => item.service.id === service.id)?.quantity || 0
      const available = Math.max(0, Math.min(quantity, service.availableQuantity ?? quantity))
      setInventoryQuantity(service, available + unavailable)
      const item = inventoryItemsById.value[service.id]
      if (item) {
        item.unavailableQuantity = unavailable
      }
    }

    function removeUnavailableInventory(serviceId: number) {
      const item = inventoryItemsById.value[serviceId]
      if (item) {
        setInventoryQuantity(item.service, item.quantity - getUnavailableQuantity(item))
      }
    }

    function syncInventoryServices(services: IBookingService[], complete = false) {
      const servicesById = new Map(services.map(service => [service.id, service]))

      for (const item of inventoryItems.value) {
        const service = servicesById.get(item.service.id)
        if (!service) {
          if (complete) {
            setInventoryQuantity({ ...item.service, availableQuantity: 0 }, item.quantity)
          }
          continue
        }

        setInventoryQuantity(service, item.quantity)
      }
    }

    function clearInventory() {
      inventoryItemsById.value = {}
      inventoryServiceIds.value = []
    }

    return {
      companyAlias,
      companyCarts,
      setCompany,
      period,
      inventoryItemsById,
      firstInventoryServiceId,
      inventoryServiceIds,
      inventoryItems,
      availableInventoryItems,
      unavailableInventoryItems,
      unavailableQuantity,
      totalQuantity,
      setPeriod,
      getInventoryQuantity,
      setInventoryQuantity,
      setAvailableInventoryQuantity,
      removeUnavailableInventory,
      syncInventoryServices,
      clearInventory,
    }
  },
  {
    persist: true,
  },
)
